import type {
    CreateExtArgs,
    DalItem,
    DalValue,
    DmApis,
    Extension,
    ExtensionAPI,
    InitializeExtArgs
} from '@wix/document-manager-core'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {
    BuilderPresets,
    BuilderStyle,
    BuilderTypes,
    CompRef,
    InnerElementPath,
    InnerElementsMap,
    NestedDataPathSegment,
    Pointer,
    PossibleStyleForBuilderComponent,
    SerializedCompStructure,
    SlotDataType,
    SlotsDataItem,
    BoundPropertiesPerProvider,
    BindingsMap,
    TemplateBindingsMap,
    FunctionBindingsMap,
    FunctionBindingConfig,
    PopulatedComponentContainerInfoMap
} from '@wix/document-services-types'
import {BindingType, ArgumentType} from '../../constants/dsTypesConstants'
import type {VariantsExtensionAPI} from '../variants/variants'
import {stripHashIfExists} from '../../utils/refArrayUtils'
import type {SchemaExtensionAPI} from '../schema/schema'
import {ReportableError} from '@wix/document-manager-utils'
import {COMP_DATA_QUERY_KEYS_WITH_STYLE, DATA_TYPES} from '../../constants/constants'
import isNil from 'lodash/isNil'
import _ from 'lodash'
import {validateLanguageCode} from '../../utils/builderUtils'
import {EVENTS, type SlotsExtensionAPI} from '../slots/slots'
import {createContainerInfoMapFromSlotsData} from '../slots/utils'
import type {ComponentsExtensionAPI} from '../components/components'
import {normalizeCompRefVariants, removeVariantsFromPointer} from '../../utils/variantsUtils'
import type {CssStyleExtensionAPI} from '../cssStyle/cssStyle'
import {removeDataByNamespace} from '../../utils/dataUtils'
import type {StylesExtensionApi} from '../style/style'

import {
    DATA_MODEL_HOOKS,
    type InnerElementDataUpdateAfterEvent,
    type InnerElementDataUpdateBeforeEvent
} from '../dataModel/hooks'
import {builderComponentsDataUpdateHookOld} from './hooks/autoPopulateUpdateOld'
import type {HooksExtensionApi} from '../hooks/hooks'
import type {ManifestExtensionAPI} from './manifest'
import {builderComponentsRemoveSlotHook} from './hooks/autoRemoveSlot'
import {builderComponentsDataUpdateHook} from './hooks/autoPopulateUpdate'
import {getDataValueByNestedPath} from './utils'
import type {DataExtensionAPI} from '../data'
import {handleMenuItemContainerFlow, findMenuItemsManifestItem} from '../menu/menuUtils'

interface InnerElementsApi extends ExtensionAPI {
    getDefinition(compTypeOrRef: string | CompRef, innerElementPath: InnerElementPath): BuilderTypes
    getInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): DalItem | undefined
    getInnerElementsNames(compRef: CompRef, innerElementPath?: InnerElementPath): string[]
    getOrCreateInnerElement(compRef: CompRef, innerElementPath: InnerElementPath, pageId: string): DalItem
    getInnerElementData(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode?: string
    ): DalItem | undefined
    getInnerElementTranslationOverrides(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string
    ): DalItem | undefined
    setInnerElementData(compRef: CompRef, innerElementPath: InnerElementPath, data: any, languageCode?: string): void
    removeInnerElementTranslationOverrides(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string
    ): void
    removeInnerElementTranslationOverridesForAField(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string,
        field: string
    ): void
    getInnerElementStyle(compRef: CompRef, innerElementPath: InnerElementPath): PossibleStyleForBuilderComponent
    setInnerElementStyle(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        style: Partial<BuilderStyle>,
        shouldRemoveRelatedProperties?: boolean
    ): void
    connectToGlobalStyle(
        componentPointer: CompRef,
        innerElementPath: InnerElementPath,
        globalStylePointerOrId: Pointer | string
    ): void
    removeInnerElementStyleProperties(compRef: CompRef, innerElementPath: InnerElementPath, paths: string[]): void
    getInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath): BuilderPresets | undefined
    setInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath, presets: BuilderPresets): void
    serializeInnerElementPath(innerElementPath: InnerElementPath): string
    setInnerElementSlots(
        componentToAddPointer: CompRef,
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        slotName: string,
        componentDefinition: SerializedCompStructure,
        nestedDataPath?: NestedDataPathSegment[]
    ): Pointer
    addPopulatedInnerElementComponent(
        componentToAddPointer: CompRef,
        slottedComponent: CompRef,
        innerElementPath: InnerElementPath,
        componentDefinition: SerializedCompStructure
    ): InnerElementSlotPopulationContext
    updateInnerElementSlotInData(
        slottedComponent: CompRef,
        innerElementPath: InnerElementPath,
        slotName: string,
        nestedDataPath?: NestedDataPathSegment[]
    ): string
    updateInnerElementSlot(
        slottedComponent: CompRef,
        slotContext: InnerElementSlotPopulationContext,
        slotKeyName: string,
        componentToAddPointer: Pointer
    ): void
    removeInnerElementSlot(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        slotName: string,
        nestedDataPath?: NestedDataPathSegment[]
    ): void
    removeInnerElementSlotData(compRef: CompRef, innerElementPath: InnerElementPath, slotName: string): void
    getInnerElementSlotItem(compRef: CompRef, innerElementPath: InnerElementPath): DalValue | void
    getInnerElementSlots(compRef: CompRef, innerElementPath: InnerElementPath): Record<string, CompRef> | void
    removeInnerElementDataProperties(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        propsToRemove: string[]
    ): void
    getInnerElementSlotsName(compRef: CompRef, innerElementPath: InnerElementPath): string[]
    getInnerElementPopulatedComponentContainerInfoMap(
        compRef: CompRef,
        innerElementPath: InnerElementPath
    ): PopulatedComponentContainerInfoMap
    removeInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): void
    scoped: {
        removeStyle(compRef: CompRef, innerElementPath: InnerElementPath): void
    }
    bindings: {
        bind(
            componentPointer: CompRef,
            innerElementPath: InnerElementPath,
            contextType: string,
            bindingsMap: BindingsMap
        ): void
        bindAll(
            componentPointer: CompRef,
            innerElementPath: InnerElementPath,
            contextType: string,
            bindingsMap: BindingsMap
        ): void
        unbindProperties(componentPointer: CompRef, innerElementPath: InnerElementPath, properties: string[]): void
        getBoundProperties(componentPointer: CompRef, innerElementPath: InnerElementPath): BoundPropertiesPerProvider
        unbind(componentPointer: CompRef, innerElementPath: InnerElementPath): void
        functions: {
            bind(
                componentPointer: CompRef,
                innerElementPath: InnerElementPath,
                functionLibraryType: string,
                functionBindingsMap: FunctionBindingsMap
            ): void
        }
        templates: {
            bind(
                compRef: CompRef,
                innerElementPath: InnerElementPath,
                templateComponentId: string,
                bindingsMap: TemplateBindingsMap
            ): void
        }
    }
}

interface InnerElementsExtensionAPI extends ExtensionAPI {
    innerElements: InnerElementsApi
}

interface InnerElementSlotPopulationContext {
    addedComponentPointer: Pointer
    ownerOfComponentInSlotPointer: CompRef
    innerElementPointer: CompRef
    slotsDataType: SlotDataType
}

function throwError(message: string, extras: any = {}): never {
    throw new ReportableError({errorType: 'InnerElementsError', message, ...extras})
}

const createExtension = (): Extension => {
    const createExtensionAPI = (args: CreateExtArgs): InnerElementsExtensionAPI => {
        const {dal, pointers, eventEmitter, coreConfig} = args
        const {experimentInstance} = coreConfig
        const {
            schemaAPI,
            dataModel,
            variants,
            data: dataExtension,
            slots,
            components,
            cssStyle,
            style,
            hooks
        } = args.extensionAPI as SchemaExtensionAPI &
            DataModelExtensionAPI &
            VariantsExtensionAPI &
            SlotsExtensionAPI &
            ComponentsExtensionAPI &
            CssStyleExtensionAPI &
            StylesExtensionApi &
            HooksExtensionApi &
            DataExtensionAPI

        const namespace = DATA_TYPES.innerElements

        function innerElementsPointer(id: string, overrides?: Partial<Pointer>): Pointer {
            return pointers.getPointer(id, DATA_TYPES.innerElements, overrides)
        }

        function getComp(compRef: CompRef): DalItem {
            const comp = dal.get(compRef)
            if (!comp) {
                throwError('Component not found', {compRef})
            }
            return comp
        }

        function getOrCreateInnerElementsMapPointerFromComp(compRef: CompRef): Pointer {
            const comp = getComp(compRef)
            let innerElementsQuery = stripHashIfExists(comp.innerElementsQuery)
            if (!innerElementsQuery) {
                const mapType = builderTypes(comp.componentType).innerElementsMap
                const mapPointer = dataModel.components.addItem(compRef, namespace, {
                    type: mapType,
                    metaData: {
                        pageId: compRef.pageId
                    }
                })
                innerElementsQuery = mapPointer.id
            }
            return innerElementsPointer(innerElementsQuery)
        }

        function getOrCreateInnerElementsMapPointerFromInnerElement(innerElementId: string): Pointer {
            const pointer = innerElementsPointer(innerElementId)
            const innerElement = dal.get(pointer)
            if (!innerElement) {
                throwError('Inner element not found', {innerElementId})
            }
            if (!innerElement.innerElementsQuery) {
                const mapPointer = dataModel.addItem(
                    {
                        type: builderTypes(innerElement.referredType).innerElementsMap
                    },
                    namespace,
                    innerElement.metaData.pageId!
                )
                dataModel.linkDataToItemByType(
                    pointer,
                    mapPointer.id,
                    DATA_TYPES.innerElements,
                    COMP_DATA_QUERY_KEYS_WITH_STYLE.innerElements
                )
            }
            const updatedElement = dal.get(pointer)
            return innerElementsPointer(stripHashIfExists(updatedElement.innerElementsQuery!))
        }

        function getOrCreateInnerElementFromComponent(
            compRef: CompRef,
            innerElementName: string,
            pageId: string
        ): DalItem {
            const comp = getComp(compRef)
            const compType = comp.componentType
            const innerType = getReferredTypeForNestedElement(compType, innerElementName)

            const mapPointer = getOrCreateInnerElementsMapPointerFromComp(compRef)
            const map = dal.get(mapPointer)
            if (!map[DATA_TYPES.innerElements]?.[innerElementName]) {
                const ptr = dataModel.addItem(
                    {
                        type: 'InnerElement',
                        referredType: innerType
                    },
                    namespace,
                    pageId
                )

                const clonedMap = _.cloneDeep(map)
                if (!clonedMap[DATA_TYPES.innerElements]) {
                    clonedMap[DATA_TYPES.innerElements] = {}
                }
                clonedMap[DATA_TYPES.innerElements][innerElementName] = ptr.id
                dataModel.addDeserializedItem(clonedMap, DATA_TYPES.innerElements, pageId, clonedMap.id)
            }

            // Possible micro-optimization: No need for dal accesses here, we already have the info in scope
            const updatedMap = dal.get(mapPointer)
            const innerElementId = stripHashIfExists(updatedMap[DATA_TYPES.innerElements][innerElementName])
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getOrCreateInnerElementFromInnerElement(pageId: string, source: DalItem, targetName: string): DalItem {
            const mapPointer = getOrCreateInnerElementsMapPointerFromInnerElement(source.id!)
            const map = dal.get(mapPointer)
            const targetReferredType = getReferredTypeForNestedElement(source.referredType, targetName)
            if (!map[DATA_TYPES.innerElements]?.[targetName]) {
                const ptr = dataModel.addItem(
                    {
                        type: 'InnerElement',
                        referredType: targetReferredType
                    },
                    namespace,
                    pageId
                )
                const clonedMap = _.cloneDeep(map)
                if (!clonedMap[DATA_TYPES.innerElements]) {
                    clonedMap[DATA_TYPES.innerElements] = {}
                }
                clonedMap[DATA_TYPES.innerElements][targetName] = ptr.id
                dataModel.addDeserializedItem(clonedMap, DATA_TYPES.innerElements, pageId, clonedMap.id)
            }
            const updatedMap = dal.get(mapPointer)
            return dal.get(innerElementsPointer(stripHashIfExists(updatedMap[DATA_TYPES.innerElements][targetName])))
        }

        function getOrCreateInnerElement(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            pageId: string
        ): DalItem {
            const [firstName, ...restOfNames] = innerElementPath
            const firstElement = getOrCreateInnerElementFromComponent(compRef, firstName, pageId)
            return restOfNames.reduce(
                (acc, name) => getOrCreateInnerElementFromInnerElement(pageId, acc, name),
                firstElement
            )
        }

        function getInnerElementFromComponent(compRef: CompRef, innerElementName: string): DalItem | undefined {
            const comp = getComp(compRef)
            const innerElementsQuery = stripHashIfExists(comp.innerElementsQuery)
            if (!innerElementsQuery) {
                return undefined
            }
            const map = dal.get(innerElementsPointer(innerElementsQuery))
            if (!map) {
                return undefined
            }
            const innerElementId = stripHashIfExists(map[DATA_TYPES.innerElements][innerElementName])
            if (!innerElementId) {
                return undefined
            }
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getInnerElementFromInnerElement(source: DalItem, innerElementName: string): DalItem | undefined {
            const innerElementsQuery = stripHashIfExists(source.innerElementsQuery)
            if (!innerElementsQuery) {
                return undefined
            }
            const map = dal.get(innerElementsPointer(innerElementsQuery))
            if (!map) {
                return undefined
            }
            const innerElementId = stripHashIfExists(map[DATA_TYPES.innerElements][innerElementName])
            if (!innerElementId) {
                return undefined
            }
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): DalItem | undefined {
            const [firstName, ...restOfNames] = innerElementPath
            const firstElement = getInnerElementFromComponent(compRef, firstName)
            return restOfNames.reduce(
                (acc, name) => (acc ? getInnerElementFromInnerElement(acc, name) : undefined),
                firstElement
            )
        }

        function getInnerElementsNames(compRef: CompRef, innerElementPath: InnerElementPath = []): string[] {
            const ownerItem =
                innerElementPath.length > 0 ? getInnerElement(compRef, innerElementPath) : getComp(compRef)

            if (!ownerItem) {
                return []
            }
            const innerElementsQuery = stripHashIfExists(ownerItem[COMP_DATA_QUERY_KEYS_WITH_STYLE.innerElements])

            if (!innerElementsQuery) {
                return []
            }
            const {innerElements}: InnerElementsMap = dal.get(innerElementsPointer(innerElementsQuery))

            return innerElements ? Object.keys(innerElements) : []
        }

        function builderTypes(compType: string): BuilderTypes {
            return (
                schemaAPI.getDefinition(compType)?.builderTypes ??
                throwError(`No builderTypes for component type ${compType}`, {compType})
            )
        }

        function getReferredTypeForNestedElement(sourceType: string, innerElementName: string): string {
            const sourceBuilderTypes = builderTypes(sourceType)
            return (
                sourceBuilderTypes.innerElementTypes[innerElementName] ??
                throwError('Failed to get type info for innerElementType', {innerElementType: sourceType})
            )
        }

        function getInnerElementData(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode?: string
        ): DalItem | undefined {
            const innerElement = getInnerElement(compRef, innerElementPath)
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(innerElement?.dataQuery)
            return dataExtension.getInLanguage(pageId, dataId, languageCode)
        }

        function getInnerElementTranslationOverrides(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string
        ): DalItem | undefined {
            validateLanguageCode(languageCode)

            const innerElement = getInnerElement(compRef, innerElementPath)
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(innerElement?.dataQuery)

            return dataModel.getItem(dataId, DATA_TYPES.data, pageId, languageCode)
        }

        function setInnerElementData(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            dataItem: any,
            languageCode?: string
        ): void {
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)

            const expectedDataType = getExpectedDataType(compRef, innerElementPath)
            const dataId = stripHashIfExists(innerElement.dataQuery)

            hooks.executeHook(
                DATA_MODEL_HOOKS.DATA.INNER_ELEMENT_UPDATE_BEFORE.createEvent({
                    componentPointer: compRef,
                    dataItem
                })
            )

            dataExtension.updateInLanguage(
                innerElementsPointer(innerElement.id!),
                dataItem,
                pageId,
                dataId,
                languageCode,
                {refTypes: [expectedDataType]}
            )

            hooks.executeHook(
                DATA_MODEL_HOOKS.DATA.INNER_ELEMENT_UPDATE_AFTER.createEvent({
                    componentPointer: compRef,
                    dataItem
                })
            )
        }

        function addPopulatedInnerElementComponent(
            componentToAddPointer: CompRef,
            slottedComponent: CompRef,
            innerElementPath: InnerElementPath,
            componentDefinition: SerializedCompStructure
        ): InnerElementSlotPopulationContext {
            const ownerOfComponentInSlotPointer = slots.getOwnerOfComponentInSlot(slottedComponent)
            const addedComponentPointer = components.addComponentV2Internal(
                componentToAddPointer,
                slottedComponent,
                componentDefinition,
                {isPlugin: true}
            )
            const pageId = pointers.structure.getPageOfComponent(slottedComponent).id
            const innerElement = getOrCreateInnerElement(slottedComponent, innerElementPath, pageId)
            return {
                addedComponentPointer,
                ownerOfComponentInSlotPointer,
                innerElementPointer: innerElementsPointer(innerElement.id!) as CompRef,
                slotsDataType: getExpectedSlotsType(slottedComponent, innerElementPath)
            }
        }

        function updateInnerElementSlotInData(
            slottedComponent: CompRef,
            innerElementPath: InnerElementPath,
            slotName: string,
            nestedDataPath?: NestedDataPathSegment[]
        ): string {
            return slots.updateSlotInData(slottedComponent, slotName, nestedDataPath ?? [], innerElementPath)
        }

        function updateInnerElementSlot(
            slottedComponent: CompRef,
            slotContext: InnerElementSlotPopulationContext,
            slotKeyName: string,
            componentToAddPointer: Pointer
        ): void {
            slots.updateSlot(
                slotContext.innerElementPointer,
                slotKeyName,
                componentToAddPointer,
                slotContext.slotsDataType
            )
            eventEmitter.emit(
                EVENTS.SLOTS.AFTER_POPULATE,
                slottedComponent,
                slotContext.ownerOfComponentInSlotPointer,
                slotContext.addedComponentPointer
            )
        }

        function setInnerElementSlots(
            componentToAddPointer: CompRef,
            slottedComponent: CompRef,
            innerElementPath: InnerElementPath,
            slotName: string,
            componentDefinition: SerializedCompStructure,
            nestedDataPath?: NestedDataPathSegment[]
        ) {
            const slotContext = addPopulatedInnerElementComponent(
                componentToAddPointer,
                slottedComponent,
                innerElementPath,
                componentDefinition
            )
            const slotKeyName = experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')
                ? updateInnerElementSlotInData(slottedComponent, innerElementPath, slotName, nestedDataPath)
                : slotName
            updateInnerElementSlot(slottedComponent, slotContext, slotKeyName, componentToAddPointer)

            return slotContext.addedComponentPointer
        }

        function removeInnerElementSlotData(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            slotKeyName: string
        ): void {
            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return
            }

            const innerElementPointer = innerElementsPointer(innerElement.id!)
            const slotsData = slots.getSlotsDataItem(innerElementPointer as CompRef)

            const slottedComponentId = slotsData.slots[slotKeyName]

            _removeSlottedComponentFromDalAndParent(compRef, slottedComponentId)
            // @ts-ignore
            _cleanSlotDataAndInnerElementMap(compRef, innerElementPointer, innerElementPath, slotsData, slotKeyName)
        }

        function removeInnerElementSlot(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            slotName: string,
            nestedDataPath: NestedDataPathSegment[] = []
        ): void {
            let slotKeyName = slotName

            if (experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')) {
                const componentData = getInnerElementData(compRef, innerElementPath)
                slotKeyName = getDataValueByNestedPath(nestedDataPath, componentData as Record<string, any>, slotName)

                dataExtension.injectData({
                    pointer: compRef,
                    path: innerElementPath,
                    nestedDataPath,
                    partialNewData: {[slotName]: ''}
                })
            }

            removeInnerElementSlotData(compRef, innerElementPath, slotKeyName)
        }

        function _removeSlottedComponentFromDalAndParent(compRef: CompRef, slottedComponentId: string) {
            const slottedComponentPointer = pointers.getPointer(slottedComponentId, compRef.type) as CompRef
            if (dal.has(slottedComponentPointer)) {
                components.removeComponent(slottedComponentPointer)
            }
        }

        function _cleanSlotDataAndInnerElementMap(
            compRef: CompRef,
            innerElementPointer: CompRef,
            innerElementPath: string[],
            slotsData: DalValue,
            slotName: string
        ) {
            delete slotsData.slots[slotName]
            if (_.isEmpty(slotsData.slots)) {
                dataModel.components.removeItem(innerElementPointer, DATA_TYPES.slots)
                const innerElement = dal.get(innerElementPointer)
                const hasChildInnerElements =
                    innerElement?.innerElementsQuery &&
                    !_.isEmpty(
                        dal.get(innerElementsPointer(stripHashIfExists(innerElement.innerElementsQuery)))?.[
                            DATA_TYPES.innerElements
                        ]
                    )
                if (!hasChildInnerElements) {
                    _removeInnerElementFromParentMap(compRef, innerElementPath)
                }
            } else {
                dataModel.components.addItem(innerElementPointer, DATA_TYPES.slots, slotsData)
            }
        }

        function _getChildInnerElementsRecursively(
            innerElement: DalItem,
            basePath: InnerElementPath
        ): InnerElementPath[] {
            const innerElementsQuery = stripHashIfExists(innerElement.innerElementsQuery)
            if (!innerElementsQuery) {
                return []
            }
            const map = dal.get(innerElementsPointer(innerElementsQuery))
            if (!map?.[DATA_TYPES.innerElements]) {
                return []
            }

            const children = Object.entries(map[DATA_TYPES.innerElements]) as [string, string][]

            return children.reduce((acc, [name, id]) => {
                const childPath = [...basePath, name]
                const childInnerElement = dal.get(innerElementsPointer(stripHashIfExists(id)))
                if (childInnerElement) {
                    acc.push(..._getChildInnerElementsRecursively(childInnerElement, childPath))
                }
                acc.push(childPath)
                return acc
            }, [] as InnerElementPath[])
        }

        function _removeInnerElementFromParentMap(compRef: CompRef, innerElementPath: InnerElementPath): void {
            const names = innerElementPath
            const elementName = names[names.length - 1]
            let mapPointer: Pointer
            let parentPointer: Pointer

            if (names.length === 1) {
                const comp = getComp(compRef)
                const innerElementsQuery = stripHashIfExists(comp.innerElementsQuery)
                if (!innerElementsQuery) {
                    return
                }
                mapPointer = innerElementsPointer(innerElementsQuery)
                parentPointer = compRef
            } else {
                const parentPath = innerElementPath.slice(0, -1)
                const parentInnerElement = getInnerElement(compRef, parentPath)
                if (!parentInnerElement) {
                    return
                }
                mapPointer = getOrCreateInnerElementsMapPointerFromInnerElement(parentInnerElement.id!)
                parentPointer = innerElementsPointer(parentInnerElement.id!)
            }

            const innerMapPointer = pointers.getInnerPointer(mapPointer, [DATA_TYPES.innerElements, elementName])
            dal.remove(innerMapPointer)

            if (_.isEmpty(dal.get(mapPointer)?.[DATA_TYPES.innerElements])) {
                dal.set(parentPointer, _.omit(dal.get(parentPointer), [COMP_DATA_QUERY_KEYS_WITH_STYLE.innerElements]))
                dataModel.removeItemRecursively(mapPointer)
            }
        }

        function getInnerElementSlotItem(compRef: CompRef, innerElementPath: InnerElementPath): SlotsDataItem | void {
            const comp = dal.get(compRef)
            if (!comp) {
                throwError('Component not found', {compRef})
            }

            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return
            }

            const innerElementPointer = innerElementsPointer(innerElement!.id!)
            return slots.getSlotsDataItem(innerElementPointer as CompRef)
        }

        function getInnerElementSlots(
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): Record<string, CompRef> | void {
            const comp = dal.get(compRef)
            if (!comp) {
                throwError('Component not found', {compRef})
            }

            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return
            }

            const innerElementPointer = innerElementsPointer(innerElement!.id!)
            return slots.getSlotsData(innerElementPointer as CompRef, 'DESKTOP')
        }

        function getInnerElementSlotsName(compRef: CompRef, innerElementPath: InnerElementPath): string[] {
            return _.keys(getInnerElementSlots(compRef, innerElementPath))
        }

        function getInnerElementPopulatedComponentContainerInfoMap(
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): PopulatedComponentContainerInfoMap {
            const compRefWithoutVariants = removeVariantsFromPointer(compRef) as CompRef
            const slotsData = getInnerElementSlotItem(compRefWithoutVariants, innerElementPath)
            const {manifest} = args.extensionAPI as ManifestExtensionAPI
            const manifestContainersList = manifest
                .getData(compRefWithoutVariants, true, [{prop: 'container'}])
                .filter(({path}) => _.isEqual(path, innerElementPath))

            return createContainerInfoMapFromSlotsData(
                slotsData,
                manifestContainersList,
                (id, type) => pointers.getPointer(id, type),
                compRefWithoutVariants.type
            )
        }

        function removeInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): void {
            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return
            }

            const allInnerElementsToRemove = [
                ..._getChildInnerElementsRecursively(innerElement, innerElementPath),
                innerElementPath
            ]

            for (const path of allInnerElementsToRemove) {
                const containerInfoMap = getInnerElementPopulatedComponentContainerInfoMap(compRef, path)
                Object.values(containerInfoMap).forEach(({slotName}) => removeInnerElementSlot(compRef, path, slotName))
            }

            _removeInnerElementFromParentMap(compRef, innerElementPath)
            dataModel.removeItemRecursively(innerElementsPointer(innerElement.id!))
        }

        function removeInnerElementTranslationOverrides(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string
        ): void {
            validateLanguageCode(languageCode)

            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getInnerElement(compRef, innerElementPath)
            const dataId = stripHashIfExists(innerElement?.dataQuery)
            const translationOverridesPointer = pointers.multilingualTranslations.translationDataItem(
                pageId,
                languageCode,
                dataId
            )
            dataModel.removeItemRecursively(translationOverridesPointer)
        }

        function removeInnerElementTranslationOverridesForAField(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string,
            field: string
        ) {
            validateLanguageCode(languageCode)

            const translationOverridesDataItem = getInnerElementTranslationOverrides(
                compRef,
                innerElementPath,
                languageCode
            )
            if (translationOverridesDataItem) {
                delete translationOverridesDataItem[field]
                if (_.isEmpty(_.omit(translationOverridesDataItem, ['_id']))) {
                    removeInnerElementTranslationOverrides(compRef, innerElementPath, languageCode)
                } else {
                    setInnerElementData(compRef, innerElementPath, translationOverridesDataItem, languageCode)
                }
            }
        }

        /**
         * Inner element types follow a predictable naming convention generated by the server:
         * `compType|innerElementPath[0]|innerElementPath[1]|...`
         *
         * For example:
         * - compType: 'Root', path: [] -> 'Root'
         * - compType: 'Root', path: ['Parent', 'Child'] -> 'Root|Parent|Child'
         *
         * reference: https://github.com/wix-private/dm-server/blob/41fa7fac5272cf2ffba8da66c4e938a5bd8aea63/packages/schema-generator-lib/src/lib/definition/index.ts#L16-L18
         *
         * Previous implementation traversed the path iteratively, calling `builderTypes()` at each
         * step to get `innerElementTypes[name]` for the next segment. This required O(n) schema
         * lookups. Since the type naming is predictable, we can construct the target
         * type string directly and perform a single lookup.
         *
         * If the server-side type naming convention changes, `builderTypes()` will throw when looking
         * up the constructed type, making this assumption explicit and easy to debug.
         */
        function getBuilderTypesFromCompType(compType: string, innerElementPath: InnerElementPath): BuilderTypes {
            const targetType = [compType, ...innerElementPath].join('|')
            return builderTypes(targetType)
        }

        function getDefinition(compTypeOrRef: string | CompRef, innerElementPath: InnerElementPath): BuilderTypes {
            const compType = typeof compTypeOrRef === 'string' ? compTypeOrRef : getComp(compTypeOrRef).componentType
            return getBuilderTypesFromCompType(compType, innerElementPath)
        }

        function getExpectedDataType(compRef: CompRef, innerElementPath: InnerElementPath): string {
            return getDefinition(compRef, innerElementPath).data
        }

        function getExpectedStyleType(compRef: CompRef, innerElementPath: InnerElementPath): BuilderTypes['style'] {
            return getDefinition(compRef, innerElementPath).style
        }

        function getExpectedPresetsType(compRef: CompRef, innerElementPath: InnerElementPath): string {
            return getDefinition(compRef, innerElementPath).presets
        }

        function getExpectedSlotsType(compRef: CompRef, innerElementPath: InnerElementPath): SlotDataType {
            return getDefinition(compRef, innerElementPath).slots as SlotDataType
        }

        function autoType<T extends Record<string, any>>(obj: T, expectedType: string): T {
            if (isNil(obj)) {
                return obj
            }
            const typeWasExplicitlyDeclared = obj.type !== undefined
            if (typeWasExplicitlyDeclared && obj.type !== expectedType) {
                throwError('Type mismatch', {expectedType, actualType: obj.type})
            }
            return typeWasExplicitlyDeclared ? obj : {...obj, type: expectedType}
        }

        const getEmptyInnerElementStyle = (compRef: CompRef, innerElementPath: InnerElementPath): BuilderStyle => {
            const styleType = getExpectedStyleType(compRef, innerElementPath)
            return {
                type: styleType
            }
        }

        const getInnerElementStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): PossibleStyleForBuilderComponent => {
            const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)

            if (!dal.has(_.omit(compRef, ['variants']))) {
                throwError('Component not found', {compRef: compRefWithNormalizedVariants})
            }

            const innerElement = getInnerElement(compRefWithNormalizedVariants, innerElementPath)
            if (!innerElement) {
                return
            }
            return cssStyle.components.getCSSStyleByPointer(
                innerElementsPointer(innerElement.id!, {
                    variants: compRefWithNormalizedVariants.variants
                })
            )
        }

        const getResolvedInnerElementStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): BuilderStyle | undefined => {
            let styleItem = getInnerElementStyle(compRef, innerElementPath)

            if (styleItem?.type === 'GlobalStyle') {
                const globalStylePointer = style.global.getPointer(styleItem.id)
                styleItem = style.global.get(globalStylePointer) as BuilderStyle
            }

            return styleItem
        }

        function removeInnerElementDataProperties(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            propsToRemove: string[]
        ): void {
            const innerElement = getInnerElement(compRef, innerElementPath)
            const innerElementPointer = innerElementsPointer(
                innerElement!.id!,
                compRef.variants ? {variants: compRef.variants} : {}
            )

            removeDataByNamespace(args.extensionAPI, innerElementPointer, DATA_TYPES.data, propsToRemove, true)
        }

        const connectToGlobalStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            globalStylePointerOrId: Pointer | string
        ): void => {
            const globalStyleDefinition = style.global.definition.get(globalStylePointerOrId)
            const expectedStyleType = getExpectedStyleType(compRef, innerElementPath)
            if (globalStyleDefinition.styleType !== expectedStyleType) {
                throw new Error(
                    `The style type ${globalStyleDefinition.styleType} is not derived from the component manifest. The inner element style type is ${expectedStyleType}`
                )
            }

            const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
            const pageId = pointers.structure.getPageOfComponent(compRefWithNormalizedVariants).id
            const innerElement = getOrCreateInnerElement(compRefWithNormalizedVariants, innerElementPath, pageId)
            const innerElementPointer = innerElementsPointer(innerElement.id!, {
                variants: compRefWithNormalizedVariants.variants
            })

            style.connectToGlobalStyle(innerElementPointer, globalStylePointerOrId)
        }

        const getBindingsListFromInnerElement = (innerElementPointer: Pointer): DalItem | undefined => {
            return dataModel.components.getItem(innerElementPointer as CompRef, DATA_TYPES.bindings)
        }

        const bind = (
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            contextType: string,
            bindingsMap: BindingsMap,
            replace: boolean
        ): void => {
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)
            const innerElementPointer = innerElementsPointer(innerElement.id!)

            const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)

            if (!existingBindingsList) {
                dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                    type: 'BindingsList',
                    data: [
                        {
                            type: 'ContextBindingConfig',
                            contextProvider: contextType,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const bindingConfigs = existingBindingsList?.data ?? []

            const configAlreadyDefined = _.find(
                bindingConfigs,
                (config: DalItem) => config.contextProvider === contextType
            )

            if (!configAlreadyDefined) {
                dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                    ...existingBindingsList,
                    data: [
                        ...bindingConfigs,
                        {
                            type: 'ContextBindingConfig',
                            contextProvider: contextType,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const updatedConfig = {
                ...configAlreadyDefined,
                properties: replace ? bindingsMap : {...configAlreadyDefined.properties, ...bindingsMap}
            }

            const updatedBindingConfigs = bindingConfigs.map((config: DalItem) =>
                config.id === updatedConfig.id ? updatedConfig : config
            )

            dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                ...existingBindingsList,
                data: updatedBindingConfigs
            })
        }

        const bindFunction = (
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            functionLibraryType: string,
            functionBindingsMap: FunctionBindingsMap
        ): void => {
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)
            const innerElementPointer = innerElementsPointer(innerElement.id!)

            for (const [propertyName, binding] of Object.entries(functionBindingsMap)) {
                const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)
                const newConfig = {
                    type: 'FunctionBindingConfig',
                    propertyName,
                    functionBindingItem: {
                        type: ArgumentType.FunctionBinding,
                        functionLibraryType,
                        function: binding.function,
                        ...binding.callOptions
                    }
                }

                if (!existingBindingsList) {
                    dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                        type: 'BindingsList',
                        data: [newConfig]
                    })
                    continue
                }

                const bindingConfigs = existingBindingsList?.data ?? []

                dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                    ...existingBindingsList,
                    data: [...bindingConfigs, newConfig]
                })
            }
        }

        const bindTemplate = (
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            templateComponentId: string,
            bindingsMap: TemplateBindingsMap
        ): void => {
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)
            const innerElementPointer = innerElementsPointer(innerElement.id!)

            if (!templateComponentId) {
                throwError('templateComponentId is required')
            }

            const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)

            if (!existingBindingsList) {
                dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                    type: 'BindingsList',
                    data: [
                        {
                            type: 'TemplateBindingConfig',
                            templateComponentId,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const bindingConfigs = existingBindingsList?.data ?? []
            const existingIndex = _.findIndex(bindingConfigs, {
                type: 'TemplateBindingConfig',
                templateComponentId
            })

            const newConfig = {
                type: 'TemplateBindingConfig',
                templateComponentId,
                properties:
                    existingIndex >= 0 ? {...bindingConfigs[existingIndex].properties, ...bindingsMap} : bindingsMap
            }

            const updatedConfigs =
                existingIndex >= 0
                    ? bindingConfigs.map((c: DalItem, i: number) => (i === existingIndex ? newConfig : c))
                    : [...bindingConfigs, newConfig]

            dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                ...existingBindingsList,
                data: updatedConfigs
            })
        }

        const unbindProperties = (compRef: CompRef, innerElementPath: InnerElementPath, properties: string[]): void => {
            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                throwError('Inner element not found', {compRef, innerElementPath})
            }

            const innerElementPointer = innerElementsPointer(innerElement!.id!)

            const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)

            if (!existingBindingsList) {
                throwError('This inner element does not have bindings', {compRef, innerElementPath})
            }

            const existingBindingConfigs = existingBindingsList?.data ?? []
            const configsToRemove = new Set<DalItem>()
            const configUpdates = new Map<DalItem, DalItem>()

            for (const config of existingBindingConfigs) {
                if (config.type === 'FunctionBindingConfig') {
                    if (properties.includes(config.propertyName)) {
                        configsToRemove.add(config)
                    }
                } else if (config.type === 'ContextBindingConfig' || config.type === 'TemplateBindingConfig') {
                    const shouldUpdateConfig = _.some(Object.keys(config.properties), (key: string) =>
                        properties.includes(key)
                    )
                    if (!shouldUpdateConfig) {
                        continue
                    }
                    const newProperties = _.omit(config.properties, properties)
                    if (_.isEmpty(newProperties)) {
                        configsToRemove.add(config)
                    } else {
                        const updatedConfig = {
                            ...config,
                            properties: newProperties
                        } as DalItem
                        configUpdates.set(config, updatedConfig)
                    }
                } else {
                    throw new Error(`unexpected binding type when calling unbindProperties: ${config.type}`)
                }
            }

            if (configsToRemove.size > 0 || configUpdates.size > 0) {
                const updatedConfigs = existingBindingConfigs
                    .filter((config: DalItem) => !configsToRemove.has(config))
                    .map((config: DalItem) => configUpdates.get(config) ?? config)
                if (_.isEmpty(updatedConfigs)) {
                    dataModel.components.removeItem(innerElementPointer, DATA_TYPES.bindings)
                } else {
                    dataModel.components.addItem(innerElementPointer, DATA_TYPES.bindings, {
                        ...existingBindingsList,
                        data: updatedConfigs
                    })
                }
            }
        }

        const getBoundProperties = (
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): BoundPropertiesPerProvider => {
            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                return {}
            }

            const innerElementPointer = innerElementsPointer(innerElement.id!)
            const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)

            if (!existingBindingsList?.data || existingBindingsList.data.length === 0) {
                return {}
            }

            const bindings: BoundPropertiesPerProvider = {}

            for (const config of existingBindingsList.data) {
                const dalItem = config as DalItem
                if (dalItem.type === 'FunctionBindingConfig') {
                    const functionConfig = dalItem as unknown as FunctionBindingConfig & {type: string}
                    const newEntry = {
                        [functionConfig.propertyName]: {
                            function: functionConfig.functionBindingItem.function,
                            callOptions: {
                                asCallback: functionConfig.functionBindingItem.asCallback,
                                arguments: functionConfig.functionBindingItem.arguments
                            }
                        }
                    }
                    if (!bindings[functionConfig.functionBindingItem.functionLibraryType]) {
                        bindings[functionConfig.functionBindingItem.functionLibraryType] = {
                            bindingType: BindingType.FunctionLibrary,
                            functionLibraryType: functionConfig.functionBindingItem.functionLibraryType,
                            bindingsMap: newEntry
                        }
                        continue
                    }
                    bindings[functionConfig.functionBindingItem.functionLibraryType].bindingsMap[
                        functionConfig.propertyName
                    ] = newEntry[functionConfig.propertyName]
                } else if (dalItem.type === 'ContextBindingConfig') {
                    bindings[dalItem.contextProvider] = {
                        bindingType: BindingType.ContextProvider,
                        contextType: dalItem.contextProvider,
                        bindingsMap: dalItem.properties
                    }
                } else if (dalItem.type === 'TemplateBindingConfig') {
                    bindings[dalItem.templateComponentId] = {
                        bindingType: BindingType.Template,
                        templateComponentId: dalItem.templateComponentId,
                        bindingsMap: dalItem.properties
                    }
                } else {
                    throw new Error(`unexpected binding type when calling getBoundProperties: ${dalItem.type}`)
                }
            }

            return bindings
        }

        const unbind = (compRef: CompRef, innerElementPath: InnerElementPath): void => {
            const innerElement = getInnerElement(compRef, innerElementPath)
            if (!innerElement) {
                throwError('Inner element not found', {compRef, innerElementPath})
            }

            const innerElementPointer = innerElementsPointer(innerElement!.id!)
            const existingBindingsList = getBindingsListFromInnerElement(innerElementPointer)
            if (!existingBindingsList) {
                return
            }

            dataModel.components.removeItem(innerElementPointer as CompRef, DATA_TYPES.bindings)
        }

        return {
            innerElements: {
                getDefinition,
                getInnerElement,
                getInnerElementsNames,
                getOrCreateInnerElement,
                serializeInnerElementPath(innerElementPath: InnerElementPath) {
                    return innerElementPath.join('|')
                },
                getInnerElementData,
                getInnerElementTranslationOverrides,
                setInnerElementData,
                removeInnerElementTranslationOverrides,
                removeInnerElementTranslationOverridesForAField,
                getInnerElementStyle,
                setInnerElementStyle(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    partialStyle: Partial<BuilderStyle>,
                    shouldRemoveRelatedProperties = true
                ) {
                    const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
                    const pageId = pointers.structure.getPageOfComponent(compRefWithNormalizedVariants).id
                    const innerElement = getOrCreateInnerElement(
                        compRefWithNormalizedVariants,
                        innerElementPath,
                        pageId
                    )
                    const existingStyle =
                        getResolvedInnerElementStyle(compRefWithNormalizedVariants, innerElementPath) ??
                        getEmptyInnerElementStyle(compRefWithNormalizedVariants, innerElementPath)

                    const valueToUpdate = cssStyle.getUpdatedCSSStyleItemForUpdate(
                        existingStyle,
                        partialStyle,
                        shouldRemoveRelatedProperties
                    )
                    variants.updateDataConsideringVariants({
                        pointerWithVariants: innerElementsPointer(innerElement.id!, {
                            variants: compRefWithNormalizedVariants.variants
                        }),
                        valueToUpdate,
                        namespace: DATA_TYPES.theme,
                        referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.style
                    })
                },
                connectToGlobalStyle,
                removeInnerElementStyleProperties(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    paths: string[]
                ) {
                    const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
                    const innerElement = getInnerElement(compRefWithNormalizedVariants, innerElementPath)
                    if (!innerElement?.styleId) {
                        return
                    }

                    const styleItem = getResolvedInnerElementStyle(compRefWithNormalizedVariants, innerElementPath)
                    if (styleItem) {
                        const valueToUpdate = cssStyle.getUpdatedCSSStyleItemForRemove(styleItem, paths)
                        if (valueToUpdate) {
                            variants.updateDataConsideringVariants({
                                pointerWithVariants: innerElementsPointer(innerElement.id!, {
                                    variants: compRefWithNormalizedVariants.variants
                                }),
                                valueToUpdate,
                                namespace: DATA_TYPES.theme,
                                referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.style
                            })
                        }
                    }
                },
                scoped: {
                    removeStyle(compRef: CompRef, innerElementPath: InnerElementPath) {
                        const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
                        const innerElement = getInnerElement(compRefWithNormalizedVariants, innerElementPath)
                        if (!innerElement?.styleId) {
                            return
                        }
                        variants.removeComponentDataConsideringVariants(
                            innerElementsPointer(innerElement.id!, {variants: compRefWithNormalizedVariants.variants}),
                            DATA_TYPES.theme
                        )
                    }
                },
                getInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath) {
                    const comp = dal.get(compRef)
                    if (!comp) {
                        throwError('Component not found', {compRef})
                    }

                    const innerElement = getInnerElement(compRef, innerElementPath)
                    if (!innerElement?.presetsQuery) {
                        return undefined
                    }

                    return variants.getComponentItemConsideringVariants(
                        innerElementsPointer(innerElement.id!, {variants: compRef.variants}),
                        DATA_TYPES.presets
                    )
                },
                setInnerElementPresets(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    presets: BuilderPresets
                ): void {
                    const pageId = pointers.structure.getPageOfComponent(compRef).id
                    const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)
                    const expectedPresetsType = getExpectedPresetsType(compRef, innerElementPath)
                    variants.updateDataConsideringVariants({
                        pointerWithVariants: innerElementsPointer(innerElement.id!, {
                            variants: compRef.variants
                        }),
                        valueToUpdate: autoType(presets, expectedPresetsType),
                        namespace: DATA_TYPES.presets,
                        referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.presets
                    })
                },
                addPopulatedInnerElementComponent,
                updateInnerElementSlotInData,
                updateInnerElementSlot,
                setInnerElementSlots,
                removeInnerElementSlot,
                removeInnerElementSlotData,
                getInnerElementSlots,
                getInnerElementSlotItem,
                getInnerElementSlotsName,
                getInnerElementPopulatedComponentContainerInfoMap,
                removeInnerElementDataProperties,
                removeInnerElement,
                bindings: {
                    bind: (
                        compRef: CompRef,
                        innerElementPath: InnerElementPath,
                        contextType: string,
                        bindingsMap: BindingsMap
                    ) => bind(compRef, innerElementPath, contextType, bindingsMap, false),
                    bindAll: (
                        compRef: CompRef,
                        innerElementPath: InnerElementPath,
                        contextType: string,
                        bindingsMap: BindingsMap
                    ) => bind(compRef, innerElementPath, contextType, bindingsMap, true),
                    unbindProperties,
                    getBoundProperties,
                    unbind,
                    functions: {
                        bind: bindFunction
                    },
                    templates: {
                        bind: bindTemplate
                    }
                }
            }
        }
    }

    const createPointersMethods = ({dal, pointers}: DmApis) => {
        const getInnerElement = (id: string, pageId?: string) =>
            pointers.data.getItem(DATA_TYPES.innerElements, id, pageId)

        const getInnerElementsMap = (innerElementPointer: Pointer) => {
            const {innerElements} =
                (dal.get(
                    getInnerElement(
                        dal.getWithPath(innerElementPointer, [
                            COMP_DATA_QUERY_KEYS_WITH_STYLE[DATA_TYPES.innerElements]
                        ])
                    )
                ) as InnerElementsMap) ?? {}

            return Object.values(innerElements ?? []).map((innerElementId: string) =>
                getInnerElement(stripHashIfExists(innerElementId))
            )
        }

        const getInnerElements = (innerElementPointer: Pointer, results: Pointer[], shouldSkip = false): void => {
            const innerElements = getInnerElementsMap(innerElementPointer)

            for (const innerElement of innerElements) {
                getInnerElements(innerElement, results)
            }

            if (!shouldSkip) {
                results.push(innerElementPointer)
            }
        }

        const getComponentInnerElementsRecursively = (componentPointer: CompRef): Pointer[] => {
            const results: Pointer[] = []
            getInnerElements(componentPointer, results, true)
            return results
        }

        return {
            innerElements: {
                getComponentInnerElementsRecursively
            }
        }
    }

    const createPublicAPI = (dmApis: CreateExtArgs) => {
        const {coreConfig, extensionAPI} = dmApis
        const {innerElements} = extensionAPI as InnerElementsExtensionAPI
        const {experimentInstance} = coreConfig

        return {
            innerElements: {
                getDefinition: (compTypeOrRef: string | CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.getDefinition(compTypeOrRef, innerElementPath),
                getInnerElementTranslationOverrides: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    languageCode: string
                ) => innerElements.getInnerElementTranslationOverrides(compRef, innerElementPath, languageCode),
                removeInnerElementTranslationOverrides: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    languageCode: string
                ) => innerElements.removeInnerElementTranslationOverrides(compRef, innerElementPath, languageCode),
                removeInnerElementTranslationOverridesForAField: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    languageCode: string,
                    field: string
                ) =>
                    innerElements.removeInnerElementTranslationOverridesForAField(
                        compRef,
                        innerElementPath,
                        languageCode,
                        field
                    ),
                removeInnerElementDataProperties: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    propsToRemove: string[]
                ) => innerElements.removeInnerElementDataProperties(compRef, innerElementPath, propsToRemove),
                serializeInnerElementPath: (innerElementPath: InnerElementPath) =>
                    innerElements.serializeInnerElementPath(innerElementPath),
                getInnerElementData: (compRef: CompRef, innerElementPath: InnerElementPath, languageCode?: string) =>
                    innerElements.getInnerElementData(compRef, innerElementPath, languageCode),
                setInnerElementData: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    data: any,
                    languageCode?: string
                ) => {
                    const menuItemsManifestItem = findMenuItemsManifestItem(dmApis, compRef, innerElementPath)
                    if (menuItemsManifestItem) {
                        handleMenuItemContainerFlow(dmApis, compRef, menuItemsManifestItem, data, languageCode)
                        return
                    }

                    innerElements.setInnerElementData(compRef, innerElementPath, data, languageCode)
                },
                getInnerElementStyle: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.getInnerElementStyle(compRef, innerElementPath),
                setInnerElementStyle: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    style: Partial<BuilderStyle>,
                    shouldRemoveRelatedProperties?: boolean
                ) =>
                    innerElements.setInnerElementStyle(compRef, innerElementPath, style, shouldRemoveRelatedProperties),
                connectToGlobalStyle: (
                    componentPointer: CompRef,
                    innerElementPath: InnerElementPath,
                    globalStylePointerOrId: Pointer | string
                ) => innerElements.connectToGlobalStyle(componentPointer, innerElementPath, globalStylePointerOrId),
                removeInnerElementStyleProperties: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    paths: string[]
                ) => innerElements.removeInnerElementStyleProperties(compRef, innerElementPath, paths),
                getInnerElementPresets: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.getInnerElementPresets(compRef, innerElementPath),
                setInnerElementPresets: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    presets: BuilderPresets
                ) => innerElements.setInnerElementPresets(compRef, innerElementPath, presets),
                setInnerElementSlots: (
                    componentToAddPointer: CompRef,
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    slotName: string,
                    componentDefinition: SerializedCompStructure,
                    nestedDataPath?: NestedDataPathSegment[]
                ) => {
                    const {manifest} = extensionAPI as ManifestExtensionAPI
                    manifest.validateContainerTypeForPopulation(compRef, slotName, nestedDataPath, innerElementPath)
                    return innerElements.setInnerElementSlots(
                        componentToAddPointer,
                        compRef,
                        innerElementPath,
                        slotName,
                        componentDefinition,
                        nestedDataPath
                    )
                },
                removeInnerElementSlot: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    slotName: string,
                    nestedDataPath: NestedDataPathSegment[] = []
                ) => {
                    const {manifest} = extensionAPI as ManifestExtensionAPI
                    if (experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')) {
                        manifest.validateContainerTypeForRemove(compRef, slotName, nestedDataPath, innerElementPath)
                    } else {
                        const slotType = manifest.innerElements.getInnerElementSlotType(
                            compRef,
                            innerElementPath,
                            slotName
                        )
                        if (slotType !== 'slot') {
                            throwError(
                                `The api removeInnerElementSlot must only be used on container of type "slot", got type "${slotType}"`
                            )
                        }
                    }
                    return innerElements.removeInnerElementSlot(compRef, innerElementPath, slotName, nestedDataPath)
                },
                getInnerElementSlots: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.getInnerElementSlots(compRef, innerElementPath),
                getInnerElementPopulatedComponentContainerInfoMap: (
                    compRef: CompRef,
                    innerElementPath: InnerElementPath
                ) => innerElements.getInnerElementPopulatedComponentContainerInfoMap(compRef, innerElementPath),
                scoped: {
                    removeStyle: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                        innerElements.scoped.removeStyle(compRef, innerElementPath)
                },
                removeInnerElement: (compRef: CompRef, innerElementPath: InnerElementPath) =>
                    innerElements.removeInnerElement(compRef, innerElementPath),
                bindings: {
                    bind: innerElements.bindings.bind,
                    bindAll: innerElements.bindings.bindAll,
                    unbindProperties: innerElements.bindings.unbindProperties,
                    getBoundProperties: innerElements.bindings.getBoundProperties,
                    unbind: innerElements.bindings.unbind,
                    functions: {
                        bind: innerElements.bindings.functions.bind
                    },
                    templates: {
                        bind: innerElements.bindings.templates.bind
                    }
                }
            }
        }
    }

    return {
        name: 'innerElements',
        dependencies: new Set(['schema', 'data', 'dataModel', 'slots', 'variants', 'cssStyle']),
        createExtensionAPI,
        createPointersMethods,
        createPublicAPI,
        initialize: async (extArgs: InitializeExtArgs) => {
            const {extensionAPI, coreConfig} = extArgs
            const {hooks} = extensionAPI as HooksExtensionApi
            const {experimentInstance} = coreConfig

            if (experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')) {
                hooks.registerHookWithExtensionArgs(
                    DATA_MODEL_HOOKS.DATA.INNER_ELEMENT_UPDATE_AFTER.id,
                    (_extArgs, {componentPointer, dataItem}: InnerElementDataUpdateAfterEvent) => {
                        return builderComponentsDataUpdateHook(extArgs, componentPointer, dataItem)
                    }
                )
                hooks.registerHookWithExtensionArgs(
                    DATA_MODEL_HOOKS.DATA.INNER_ELEMENT_UPDATE_BEFORE.id,
                    (_extArgs, {componentPointer, dataItem}: InnerElementDataUpdateBeforeEvent) => {
                        return builderComponentsRemoveSlotHook(extArgs, componentPointer, dataItem)
                    }
                )
            }
            if (!experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')) {
                hooks.registerHookWithExtensionArgs(
                    DATA_MODEL_HOOKS.DATA.INNER_ELEMENT_UPDATE_BEFORE.id,
                    (_extArgs, {componentPointer, dataItem}: InnerElementDataUpdateBeforeEvent) => {
                        return builderComponentsDataUpdateHookOld(extArgs, componentPointer, dataItem)
                    }
                )
            }
        }
    }
}

export {createExtension}
export type {InnerElementsExtensionAPI, InnerElementsApi, InnerElementPath}
